import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormRoot,
  Input,
  Label,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle, Loader2, Sparkles, User } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as RHF from "react-hook-form";
import { z } from "zod";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { config } from "@/shared/lib/config";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  useCreateLeaveEntitlementMutation,
  useLeaveTypesQuery,
} from "../api/queries";
import {
  LeaveEntitlementCreateTour,
  useLeaveEntitlementCreateTour,
} from "../tour/LeaveEntitlementCreateTour";
import {
  type LeaveEntitlementCreateFormSample,
  leaveEntitlementCreateFormSamples,
} from "../ui/leaveEntitlementCreateFormSamples";

const isDev =
  typeof import.meta !== "undefined" &&
  (import.meta.hot !== undefined || import.meta.env?.MODE === "development");

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR + i).map(
  (y) => ({
    value: String(y),
    label: String(y),
  }),
);

type FormValues = {
  employeeId: string;
  year: number;
  daysByType: Record<string, string>;
};

const LeaveEntitlementCreateFormSchema = z.object({
  employeeId: z.string().min(1, "ກະລຸນາເລືອກພະນັກງານ"),
  year: z.number(),
  daysByType: z.record(z.string(), z.string()),
});

export function LeaveEntitlementCreatePage() {
  const nav = useNavigate({ from: "/app/hr/leave/entitlements/new" });
  const createMutation = useCreateLeaveEntitlementMutation();
  const tour = useLeaveEntitlementCreateTour();
  const typesQuery = useLeaveTypesQuery({ isActive: true, limit: 100 });
  const leaveTypes = typesQuery.data?.data ?? [];
  const [sampleIndex, setSampleIndex] = useState(0);

  const methods = RHF.useForm<FormValues>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference
    resolver: zodResolver(LeaveEntitlementCreateFormSchema) as any,
    defaultValues: {
      employeeId: "",
      year: CURRENT_YEAR,
      daysByType: {},
    },
  });

  const leaveTypeIds = useMemo(
    () => leaveTypes.map((t) => t.id).join(","),
    [leaveTypes],
  );

  // Set daysByType keys when leave types load
  useEffect(() => {
    if (leaveTypes.length === 0) return;
    const next = Object.fromEntries(leaveTypes.map((t) => [t.id, ""]));
    const prev = methods.getValues();
    methods.reset(
      { ...prev, daysByType: { ...next, ...prev.daysByType } },
      { keepDefaultValues: false },
    );
  }, [leaveTypeIds, methods]);

  const handleBack = useCallback(() => {
    nav({
      to: "/app/hr/leave/entitlements",
      search: {
        offset: 0,
        limit: 20,
        sort: undefined,
        year: CURRENT_YEAR,
        leaveTypeId: undefined,
        search: undefined,
      },
    });
  }, [nav]);

  const handleAutoFill = useCallback(() => {
    const sample: LeaveEntitlementCreateFormSample | undefined =
      leaveEntitlementCreateFormSamples[sampleIndex];
    if (!sample || leaveTypes.length === 0) return;
    methods.setValue("year", sample.year);
    const next: Record<string, string> = {};
    for (const t of leaveTypes) {
      const days = sample.daysByCode[t.code];
      next[t.id] = days != null ? String(days) : "";
    }
    methods.setValue("daysByType", next);
    setSampleIndex(
      (prev) => (prev + 1) % leaveEntitlementCreateFormSamples.length,
    );
    toast.success("ເພີ່ມຂໍ້ມູນຕົວຢ່າງແລ້ວ. ກະລຸນາເລືອກພະນັກງານ.");
  }, [sampleIndex, leaveTypes, methods]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const employeeId = values.employeeId?.trim();
      if (!employeeId) {
        toast.error("ກະລຸນາເລືອກພະນັກງານ");
        return;
      }
      const year = values.year;
      const entries = leaveTypes
        .map((t) => {
          const raw = values.daysByType?.[t.id];
          const days = raw === "" || raw == null ? Number.NaN : Number(raw);
          return { leaveTypeId: t.id, totalDays: days };
        })
        .filter((e) => Number.isInteger(e.totalDays) && e.totalDays > 0);

      if (entries.length === 0) {
        toast.error("ກະລຸນາໃສ່ຈຳນວນວັນລາຢ່າງໜ້ອຍໜຶ່ງປະເພດ");
        return;
      }

      let created = 0;
      for (const { leaveTypeId, totalDays } of entries) {
        try {
          await createMutation.mutateAsync({
            employeeId,
            leaveTypeId,
            year,
            totalDays,
          });
          created++;
        } catch (err: unknown) {
          const msg =
            err && typeof err === "object" && "message" in err
              ? String((err as { message: string }).message)
              : "";
          const status =
            err && typeof err === "object" && "status" in err
              ? (err as { status?: number }).status
              : undefined;
          if (status === 409 || /already exists|unique|duplicate/i.test(msg)) {
            toast.error("ສິດລາສຳລັບພະນັກງານ ແລະ ປີນີ້ມີແລ້ວ ບາງລາຍການອາດຖືກຂ້າມ");
          } else {
            toast.error(msg || "ເກີດຂໍ້ຜິດພາດ");
          }
          break;
        }
      }

      if (created > 0) {
        toast.success(`ສ້າງສິດລາ ${created} ລາຍການສຳເລັດ`);
        nav({
          to: "/app/hr/leave/entitlements/employee/$employeeId",
          params: { employeeId },
          search: { year },
        });
      }
    },
    [leaveTypes, createMutation, nav],
  );

  return (
    <>
      <Header />
      <Main>
        <LeaveEntitlementCreateTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex items-center justify-between gap-2"
          data-tourid="leave-entitlement-create-back"
        >
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-semibold text-2xl">ເພີ່ມສິດລາຕໍ່ປີ</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
                title="ເລີ່ມທົດສອບການນຳທາງ"
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="text-muted-foreground text-sm">
              ກຳນົດສິດລາສຳລັບພະນັກງານ ແລະ ປີ
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            ກັບໄປລາຍການ
          </Button>
        </div>

        <div
          className="mx-auto max-w-2xl space-y-6"
          data-tourid="leave-entitlement-create-form"
        >
          <div>
            <h1 className="font-semibold text-2xl">ສ້າງສິດລາຕໍ່ປີ (ພະນັກງານໃໝ່)</h1>
            <p className="text-muted-foreground text-sm">
              ເລືອກພະນັກງານ ແລະ ປີ ແລ້ວກຳນົດຈຳນວນວັນລາແຕ່ລະປະເພດ
            </p>
          </div>

          <FormRoot<FormValues>
            methods={methods}
            onSubmit={onSubmit}
            className="w-full space-y-6"
          >
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <User className="h-4 w-4" />
                  ພະນັກງານ ແລະ ປີ
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormInfiniteCombobox<LookupItem>
                  name="employeeId"
                  label="ພະນັກງານ"
                  requiredMark
                  queryKey={["users-lookup-leave-entitlement-create"]}
                  queryFn={(args) =>
                    fetchLookupForInfinite(
                      `${config.apiUrl}/users/lookup`,
                      args,
                    )
                  }
                  preloadQueryFn={(id) =>
                    hydrateLookupItem(`${config.apiUrl}/users/lookup`, id)
                  }
                  getLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  placeholder="ເລືອກພະນັກງານ..."
                />
                <RHF.Controller
                  name="year"
                  control={methods.control}
                  render={({ field }) => (
                    <div className="space-y-2">
                      <Label>ປີ</Label>
                      <SimpleSelect
                        options={YEAR_OPTIONS}
                        value={String(field.value)}
                        onValueChange={(v) => field.onChange(Number(v))}
                        placeholder="ເລືອກປີ"
                        className="w-full"
                        aria-label="ປີ"
                      />
                    </div>
                  )}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">ຈຳນວນວັນລາແຕ່ລະປະເພດ</CardTitle>
                <p className="text-muted-foreground text-sm">
                  ໃສ່ຈຳນວນວັນທີ່ສາມາດລາໄດ້ — ບໍ່ໃສ່ = ບໍ່ສ້າງສິດລາປະເພດນີ້
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {leaveTypes.map((t) => (
                  <RHF.Controller
                    key={t.id}
                    name={`daysByType.${t.id}`}
                    control={methods.control}
                    render={({ field }) => (
                      <div className="flex items-center gap-4">
                        <Label className="min-w-[180px] font-normal">
                          {t.name}
                          {t.code ? ` (${t.code})` : ""}
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          step={1}
                          placeholder="0"
                          className="w-24"
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                        <span className="text-muted-foreground text-sm">
                          ວັນ
                        </span>
                      </div>
                    )}
                  />
                ))}
                {leaveTypes.length === 0 && typesQuery.isLoading && (
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    ກຳລັງໂຫຼດປະເພດການລາ...
                  </div>
                )}
                {leaveTypes.length === 0 && !typesQuery.isLoading && (
                  <p className="text-muted-foreground text-sm">ບໍ່ມີປະເພດການລາ</p>
                )}
              </CardContent>
            </Card>

            <div className="flex flex-wrap items-center gap-2">
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                ສ້າງສິດລາ
              </Button>
              <Button type="button" variant="outline" onClick={handleBack}>
                ຍົກເລີກ
              </Button>
              {isDev &&
                leaveEntitlementCreateFormSamples.length > 0 &&
                leaveTypes.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAutoFill}
                    disabled={createMutation.isPending}
                    className="gap-1.5"
                  >
                    <Sparkles className="h-4 w-4" />
                    ຕື່ມຕົວຢ່າງ ({sampleIndex + 1}/
                    {leaveEntitlementCreateFormSamples.length})
                  </Button>
                )}
            </div>
          </FormRoot>
        </div>
      </Main>
    </>
  );
}
